import React, { useEffect, useState } from "react";
import API_URL from "../../apiConfig";
import AdminHeader from "../../components/AdminHeader";
import AdminPageHeader from "../../components/admin/AdminPageHeader";

function getSubscriptionEndDate(s) {
  return s?.expires_at || s?.current_period_end || s?.trial_end || null;
}

function getSubscriptionStartDate(s) {
  return s?.current_period_start || s?.trial_start || s?.created_at || null;
}

function isManualAccess(s) {
  return String(s?.source || "").toLowerCase() === "manual";
}

function isStripeAccess(s) {
  return String(s?.source || "").toLowerCase() === "stripe" || Boolean(s?.stripe_subscription_id);
}

function isLifetime(s) {
  return (
    isManualAccess(s) &&
    !s?.expires_at &&
    !s?.current_period_end &&
    s?.status === "active"
  );
}

function isRevokedManualPermanent(s) {
  const status = String(s?.status || "").toLowerCase();

  return (
    isManualAccess(s) &&
    !isStripeAccess(s) &&
    ["canceled", "cancelled", "manual_canceled", "revoked", "expired"].includes(status)
  );
}

function hasPaidPeriodAfterTrial(s) {
  if (!s?.trial_end) return false;

  const paidEndDate = s.expires_at || s.current_period_end;
  if (!paidEndDate) return false;

  return new Date(paidEndDate).getTime() > new Date(s.trial_end).getTime() + 60 * 1000;
}

function getAccessTypeCode(s) {
  if (isLifetime(s) || isRevokedManualPermanent(s)) return "permanent";

  if (s?.status === "trialing") return "trial_7";

  if (s?.status === "past_due") return "monthly";

  if (s?.status === "active") return "monthly";

  if (s?.status === "canceled") {
    if (hasPaidPeriodAfterTrial(s) || !s?.trial_end) return "monthly";
    return "trial_7";
  }

  if (s?.trial_end && !hasPaidPeriodAfterTrial(s)) return "trial_7";

  return "monthly";
}

function renderAccessTypeLabel(s) {
  const type = getAccessTypeCode(s);

  if (type === "trial_7") return "Prueba 7";
  if (type === "monthly") return "Mensual";
  if (type === "permanent") return "Permanente";

  return "—";
}

function getCrmStatusCode(s) {
  if (!s) return "unknown";

  const accessType = getAccessTypeCode(s);

  if (accessType === "permanent") {
    if (isRevokedManualPermanent(s)) return "permanent_revoked";
    return "permanent";
  }

  if (accessType === "trial_7") {
    if (s.cancel_at_period_end && s.status === "trialing") return "trial_canceling";
    if (s.status === "trialing") return "trialing";
    if (s.status === "canceled" || s.status === "expired") return "trial_finished";
    return "trial_finished";
  }

  if (s.status === "past_due") return "past_due";

  if (s.cancel_at_period_end && s.status === "active") {
    return "active_canceling";
  }

  if (s.status === "active") return "active";

  if (s.status === "canceled") return "finished";

  if (s.status === "expired") return "finished";

  if (s.status === "manual_canceled") return "canceled";

  return "unknown";
}

function renderStatusLabel(s) {
  const status = getCrmStatusCode(s);

  if (status === "trialing") return "En prueba";
  if (status === "trial_canceling") return "Prueba cancelada";
  if (status === "trial_finished") return "Prueba finalizada";
  if (status === "active") return "Activa";
  if (status === "active_canceling") return "Cancelación programada";
  if (status === "past_due") return "Pago pendiente";
  if (status === "canceled") return "Cancelada";
  if (status === "finished") return "Finalizada";
  if (status === "permanent") return "Permanente";
  if (status === "permanent_revoked") return "Revocado";

  return "Desconocido";
}

function formatDate(dateValue) {
  if (!dateValue) return "Sin fecha";

  try {
    return new Date(dateValue).toLocaleDateString("es-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "Sin fecha";
  }
}

function SummaryCard({ label, value }) {
  return (
    <div style={summaryCardStyle}>
      <span style={summaryLabelStyle}>{label}</span>
      <strong style={summaryValueStyle}>{value}</strong>
    </div>
  );
}

export default function AdminSubscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadSubscriptions();
  }, []);

  async function loadSubscriptions() {
    try {
      const session = JSON.parse(localStorage.getItem("zedro_session"));

      const res = await fetch(`${API_URL}/api/admin/members/subscriptions`, {
        headers: {
          Authorization: `Bearer ${session?.token}`,
        },
      });

      const json = await res.json();

      if (json.ok) {
        setSubscriptions(json.subscriptions || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function extendSub(id, duration) {
    try {
      if (!duration) return;

      const session = JSON.parse(localStorage.getItem("zedro_session"));

      const res = await fetch(`${API_URL}/api/admin/members/subscriptions/${id}/extend`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token}`,
        },
        body: JSON.stringify({ duration }),
      });

      const json = await res.json();

      await loadSubscriptions();

      if (json.updated) {
        setMessage("Membresía extendida. El acceso ya existía.");
      } else {
        setMessage("Membresía extendida.");
      }

      setTimeout(() => setMessage(""), 2200);
    } catch (err) {
      console.error(err);
      alert("Error extendiendo la membresía.");
    }
  }

  async function cancelSub(subscription) {
    try {
      const isPermanentAccess = getAccessTypeCode(subscription) === "permanent";

      const confirmAction = window.confirm(
        isPermanentAccess
          ? "¿Seguro que quieres revocar este acceso permanente?"
          : "¿Seguro que quieres cancelar esta suscripción?"
      );

      if (!confirmAction) return;

      const session = JSON.parse(localStorage.getItem("zedro_session"));

      await fetch(`${API_URL}/api/admin/members/subscriptions/${subscription.id}/cancel`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session?.token}`,
        },
      });

      await loadSubscriptions();

      setMessage(
        isPermanentAccess
          ? "Acceso permanente revocado."
          : "Membresía cancelada."
      );
      setTimeout(() => setMessage(""), 2200);
    } catch (err) {
      console.error(err);
      alert("Error actualizando el acceso.");
    }
  }

  async function makeLifetime(id) {
    try {
      const confirmAction = window.confirm("¿Convertir a permanente?");
      if (!confirmAction) return;

      const session = JSON.parse(localStorage.getItem("zedro_session"));

      await fetch(`${API_URL}/api/admin/members/subscriptions/${id}/lifetime`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session?.token}`,
        },
      });

      await loadSubscriptions();

      setMessage("Membresía marcada como permanente.");
      setTimeout(() => setMessage(""), 2200);
    } catch (err) {
      console.error(err);
      alert("Error convirtiendo la membresía en permanente.");
    }
  }

  const filtered = subscriptions.filter((s) => {
    const email = s.email || "";

    const matchStatus =
      filterStatus === "all" || getCrmStatusCode(s) === filterStatus;

    const matchType =
      filterType === "all" || getAccessTypeCode(s) === filterType;

    const matchSource =
      filterSource === "all" || s.source === filterSource;

    const matchSearch =
      email.toLowerCase().includes(search.toLowerCase());

    return matchStatus && matchType && matchSource && matchSearch;
  });

  const total = subscriptions.length;
  const trial7 = subscriptions.filter((s) => getAccessTypeCode(s) === "trial_7").length;
  const monthlyActive = subscriptions.filter((s) => getAccessTypeCode(s) === "monthly" && getCrmStatusCode(s) === "active").length;
  const paymentPending = subscriptions.filter((s) => getCrmStatusCode(s) === "past_due").length;
  const scheduledCancel = subscriptions.filter((s) => getCrmStatusCode(s) === "active_canceling").length;
  const finished = subscriptions.filter((s) => ["finished", "trial_finished"].includes(getCrmStatusCode(s))).length;
  const lifetime = subscriptions.filter((s) => getCrmStatusCode(s) === "permanent").length;
  const revoked = subscriptions.filter((s) => getCrmStatusCode(s) === "permanent_revoked").length;

  function getStatusBadgeStyle(s) {
    const status = getCrmStatusCode(s);

    if (status === "active") {
      return {
        ...badgeStyle,
        background: "#eefaf3",
        color: "#166534",
        border: "1px solid #bbf7d0",
      };
    }

    if (status === "trialing") {
      return {
        ...badgeStyle,
        background: "#eff6ff",
        color: "#1d4ed8",
        border: "1px solid #bfdbfe",
      };
    }

    if (status === "trial_canceling" || status === "active_canceling") {
      return {
        ...badgeStyle,
        background: "#fffaf1",
        color: "#8a5a12",
        border: "1px solid rgba(199, 154, 69, 0.35)",
      };
    }

    if (status === "past_due") {
      return {
        ...badgeStyle,
        background: "#fff7ed",
        color: "#9a3412",
        border: "1px solid #fed7aa",
      };
    }

    if (status === "finished" || status === "trial_finished" || status === "canceled") {
      return {
        ...badgeStyle,
        background: "#f3f4f6",
        color: "#4b5563",
        border: "1px solid #e5e7eb",
      };
    }

    if (status === "permanent") {
      return {
        ...badgeStyle,
        background: "#eef2ff",
        color: "#3730a3",
        border: "1px solid #c7d2fe",
      };
    }

    if (status === "permanent_revoked") {
      return {
        ...badgeStyle,
        background: "#f8fafc",
        color: "#475569",
        border: "1px solid #cbd5e1",
      };
    }

    return {
      ...badgeStyle,
      background: "#f8fafc",
      color: "#64748b",
      border: "1px solid #e2e8f0",
    };
  }

  function getTypeBadgeStyle(s) {
    const type = getAccessTypeCode(s);

    if (type === "trial_7") {
      return {
        ...typeBadgeStyle,
        background: "#f8fafc",
        color: "#286b94",
        border: "1px solid rgba(40, 107, 148, 0.16)",
      };
    }

    if (type === "monthly") {
      return {
        ...typeBadgeStyle,
        background: "#f7f5ef",
        color: "#8a5a12",
        border: "1px solid rgba(199, 154, 69, 0.24)",
      };
    }

    if (type === "permanent") {
      return {
        ...typeBadgeStyle,
        background: "#eef2ff",
        color: "#3730a3",
        border: "1px solid #c7d2fe",
      };
    }

    return typeBadgeStyle;
  }

  function getRowStyle() {
    return {
      background: "#ffffff",
      borderBottom: "1px solid #eef2f7",
    };
  }

  if (loading) {
    return <div style={{ padding: 40 }}>Cargando...</div>;
  }

  return (
    <>
      <AdminHeader />

      <div className="admin-page" style={{ maxWidth: 1240, margin: "0 auto" }}>
        <AdminPageHeader
          title="Suscripciones"
          subtitle="Gestión global de accesos, pagos y estado de membresía."
          backTo="/admin/miembros"
        />

        <div style={summaryGridStyle}>
          <SummaryCard label="Prueba 7" value={trial7} />
          <SummaryCard label="Mensuales activas" value={monthlyActive} />
          <SummaryCard label="Pago pendiente" value={paymentPending} />
          <SummaryCard label="Cancelación programada" value={scheduledCancel} />
          <SummaryCard label="Finalizadas" value={finished} />
          <SummaryCard label="Permanentes" value={lifetime} />
          <SummaryCard label="Revocados" value={revoked} />
          <SummaryCard label="Total" value={total} />
        </div>

        <div style={filtersPanelStyle}>
          <input
            placeholder="Buscar email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={filterInputStyle}
          />

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={filterSelectStyle}
          >
            <option value="all">Todos los tipos</option>
            <option value="trial_7">Prueba 7</option>
            <option value="monthly">Mensual</option>
            <option value="permanent">Permanente</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={filterSelectStyle}
          >
            <option value="all">Todos los estados</option>
            <option value="trialing">En prueba</option>
            <option value="trial_canceling">Prueba cancelada</option>
            <option value="trial_finished">Prueba finalizada</option>
            <option value="active">Activa</option>
            <option value="active_canceling">Cancelación programada</option>
            <option value="past_due">Pago pendiente</option>
            <option value="finished">Finalizada</option>
            <option value="canceled">Cancelada</option>
            <option value="permanent">Permanente</option>
            <option value="permanent_revoked">Revocado</option>
          </select>

          <select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            style={filterSelectStyle}
          >
            <option value="all">Todas las fuentes</option>
            <option value="manual">Manual</option>
            <option value="stripe">Stripe</option>
          </select>
        </div>

        {message && (
          <div style={messageStyle}>
            {message}
          </div>
        )}

        <div style={tableWrapStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Producto</th>
                <th style={thStyle}>Tipo</th>
                <th style={thStyle}>Estado</th>
                <th style={thStyle}>Fuente</th>
                <th style={thStyle}>Inicio</th>
                <th style={thStyle}>Expira</th>
                <th style={thStyle}>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((s) => {
                const statusCode = getCrmStatusCode(s);
                const accessType = getAccessTypeCode(s);
                const canCancel = statusCode === "active" || statusCode === "permanent";
                const canMakeLifetime = accessType !== "permanent";
                const canExtend = accessType !== "permanent";

                return (
                  <tr key={s.id} style={getRowStyle(s)}>
                    <td style={tdStyle}>{s.email}</td>
                    <td style={tdStyle}>{s.product_name || "—"}</td>

                    <td style={tdStyle}>
                      <span style={getTypeBadgeStyle(s)}>
                        {renderAccessTypeLabel(s)}
                      </span>
                    </td>

                    <td style={tdStyle}>
                      <span style={getStatusBadgeStyle(s)}>
                        {renderStatusLabel(s)}
                      </span>
                    </td>

                    <td style={tdStyle}>{s.source || "—"}</td>

                    <td style={tdStyle}>{formatDate(getSubscriptionStartDate(s))}</td>

                    <td style={tdStyle}>
                      {isLifetime(s) ? "—" : formatDate(getSubscriptionEndDate(s))}
                    </td>

                    <td style={{ ...tdStyle, minWidth: 175 }}>
                      <div style={actionsCellStyle}>
                        {canExtend && (
                          <select
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => extendSub(s.id, e.target.value)}
                            style={actionSelectStyle}
                            defaultValue=""
                          >
                            <option value="">Extender acceso</option>
                            <option value="1m">+1 mes</option>
                            <option value="3m">+3 meses</option>
                            <option value="6m">+6 meses</option>
                          </select>
                        )}

                        {canCancel && (
                          <button
                            onClick={(e) => { e.stopPropagation(); cancelSub(s); }}
                            style={
                              getAccessTypeCode(s) === "permanent"
                                ? revokeActionBtnStyle
                                : dangerActionBtnStyle
                            }
                          >
                            {getAccessTypeCode(s) === "permanent" ? "Revocar acceso" : "Cancelar"}
                          </button>
                        )}

                        {canMakeLifetime && (
                          <button
                            onClick={(e) => { e.stopPropagation(); makeLifetime(s.id); }}
                            style={secondaryActionBtnStyle}
                          >
                            Hacer permanente
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

const summaryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  gap: 12,
  marginTop: 20,
};

const summaryCardStyle = {
  padding: "14px 16px",
  borderRadius: 14,
  background: "#ffffff",
  border: "1px solid #e6ebf1",
  boxShadow: "0 10px 26px rgba(12, 34, 48, 0.04)",
};

const summaryLabelStyle = {
  display: "block",
  fontSize: 12,
  color: "rgba(44, 59, 81, 0.62)",
  marginBottom: 6,
};

const summaryValueStyle = {
  fontSize: 22,
  color: "#2c3b51",
};

const filtersPanelStyle = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  marginTop: 20,
  marginBottom: 20,
};

const filterInputStyle = {
  padding: "9px 10px",
  width: "100%",
  boxSizing: "border-box",
  minWidth: 210,
  borderRadius: 10,
  border: "1px solid #d7dee8",
};

const filterSelectStyle = {
  padding: "9px 10px",
  width: "100%",
  boxSizing: "border-box",
  minWidth: 175,
  borderRadius: 10,
  border: "1px solid #d7dee8",
  background: "#ffffff",
};

const messageStyle = {
  background: "#ecfdf5",
  padding: 10,
  borderRadius: 10,
  marginBottom: 10,
  color: "#065f46",
  fontWeight: 700,
};

const tableWrapStyle = {
  overflowX: "auto",
  borderRadius: 16,
  border: "1px solid #e6ebf1",
  background: "#ffffff",
  boxShadow: "0 12px 28px rgba(12, 34, 48, 0.04)",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: 980,
};

const thStyle = {
  padding: "12px 10px",
  textAlign: "left",
  fontSize: 12,
  color: "rgba(44, 59, 81, 0.66)",
  background: "#f8fafc",
  borderBottom: "1px solid #e6ebf1",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "12px 10px",
  verticalAlign: "middle",
  fontSize: 13,
  color: "#2c3b51",
};

const badgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 96,
  padding: "5px 10px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 800,
  whiteSpace: "nowrap",
};

const typeBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 82,
  padding: "5px 9px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 800,
  whiteSpace: "nowrap",
};

const actionsCellStyle = {
  display: "flex",
  gap: 6,
  flexWrap: "wrap",
  alignItems: "center",
};

const actionSelectStyle = {
  padding: "5px 7px",
  borderRadius: 7,
  border: "1px solid #d7dee8",
  background: "#fff",
  minWidth: 118,
  fontSize: 12,
  fontWeight: 600,
};

const secondaryActionBtnStyle = {
  padding: "5px 8px",
  borderRadius: 7,
  border: "1px solid #d7dee8",
  background: "#fff",
  color: "#2c3b51",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const dangerActionBtnStyle = {
  padding: "5px 8px",
  borderRadius: 7,
  border: "1px solid #f2c7c7",
  background: "#fff7f7",
  color: "#7b1f1f",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: "nowrap",
};


const revokeActionBtnStyle = {
  padding: "5px 8px",
  borderRadius: 7,
  border: "1px solid #d7dee8",
  background: "#f8fafc",
  color: "#2c3b51",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: "nowrap",
};
